/* How the positions stand against each other.
 *
 * The screen already listed them by return from cost, which answers "which
 * pick was right". It does not answer "what actually made the money", and those
 * are different questions with different answers: a name up 300% on half a
 * percent of the book has been a better call and a smaller earner than one up
 * 20% on a third of it. A book that only ever ranks by percentage quietly
 * flatters its smallest bets.
 *
 * So both are computed, and named for what they are.
 *
 * Positions with no recorded cost are left out of every figure here rather than
 * counted at zero — an unknown cost is not a cost of nothing, and folding it in
 * would report a gain equal to the entire position. They are counted separately
 * so the screen can say how many are missing. */

import type { FullPosition } from './derive';

export interface Standing {
  ticker: string;
  /** USD. What the position is worth now less what was paid for the part of it
      that has a cost on record. */
  gainUsd: number;
  returnPct: number;
  dayChangePct: number;
  marketValue: number;
  weight: number;
}

export interface Standings {
  /** Every costed position, best return first. */
  byReturn: Standing[];
  /** The same set, largest money made first. */
  byMoney: Standing[];
  best: Standing | null;
  worst: Standing | null;
  /** The one that has made and lost the most in cash terms. */
  biggestEarner: Standing | null;
  biggestDrag: Standing | null;
  upCount: number;
  downCount: number;
  /** Summed across the costed positions. */
  totalGainUsd: number;
  /** Positions with no cost on record, and therefore in none of the above. */
  withoutCost: number;
}

const EMPTY: Standings = {
  byReturn: [], byMoney: [],
  best: null, worst: null, biggestEarner: null, biggestDrag: null,
  upCount: 0, downCount: 0, totalGainUsd: 0, withoutCost: 0,
};

export function standings(positions: FullPosition[]): Standings {
  const costed = positions.filter((p) => p.costBasis > 0 && !p.priceMissing);
  const withoutCost = positions.length - costed.length;
  if (costed.length === 0) return { ...EMPTY, withoutCost };

  const rows: Standing[] = costed.map((p) => ({
    ticker: p.ticker,
    /* Against the cost of the shares that have one. `costBasis` already covers
       only those, and `marketValue` covers the lot — so on a position part of
       which has no cost, the money figure would be overstated. Scale it to the
       share of the position that is actually costed. */
    gainUsd: p.shares > 0
      ? (p.marketValue * (p.sharesWithCost / p.shares)) - p.costBasis
      : 0,
    returnPct: p.totalReturnPct,
    dayChangePct: p.dayChangePct,
    marketValue: p.marketValue,
    weight: p.weight,
  }));

  const byReturn = [...rows].sort((a, b) => b.returnPct - a.returnPct);
  const byMoney = [...rows].sort((a, b) => b.gainUsd - a.gainUsd);

  return {
    byReturn,
    byMoney,
    best: byReturn[0] ?? null,
    worst: byReturn[byReturn.length - 1] ?? null,
    biggestEarner: byMoney[0] ?? null,
    biggestDrag: byMoney[byMoney.length - 1] ?? null,
    upCount: rows.filter((r) => r.returnPct > 0).length,
    downCount: rows.filter((r) => r.returnPct < 0).length,
    totalGainUsd: rows.reduce((sum, r) => sum + r.gainUsd, 0),
    withoutCost,
  };
}
